import { environment } from '@env/environment';
import { AuthenticationBy } from '@shared/au-enums';
import { AuPlaceholdersColorsList } from '@app/configs/user-placeholder-colors.config';
import { IAssetBuildingWithSensors } from '@app/pages/settings/portfolio/interfaces/area-assets.interfaces';

export class AuUtilsFunctions {
	static getDateObjectFromString(date: string): Date {
		if (!date) {
			throw new Error('No data provided');
		}
		return new Date(date);
	}

	// TODO Make this generic in seperate PR, too many conflicts will happen
	static deepCopyObject(object: any): any {
		if (object === null) {
			throw new Error('Trying to deep copy NULL!');
		} else if (typeof object !== 'object') {
			throw new Error('Trying to deep copy non-object value!');
		}

		return JSON.parse(JSON.stringify(object));
	}

	static getLastSegmentOfUrl(url: string): string {
		if (url?.length) {
			// delete url params
			const urlWithoutParams = url.split('?')[0];
			// find last segment
			return urlWithoutParams.split('/').slice(-1)[0];
		}
	}

	// TODO This will be dropped anyway
	static mergeInstallationToBuildingSensors(
		buildings: IAssetBuildingWithSensors[]
	): IAssetBuildingWithSensors[] {
		const buildingsDeepCopy: IAssetBuildingWithSensors[] =
			AuUtilsFunctions.deepCopyObject(buildings);

		buildingsDeepCopy.forEach(building => {
			const installationItems = building.floor
				.map(floor => floor.room)
				.flat()
				.map(room => {
					return room.installation
						.filter(installation => installation.status === 'READY')
						.map(installation => {
							return {
								id: installation.id,
								name: installation.name,
								description: '',
								sensorManufacturer: installation.sensorManufacturer,
								sensorType: installation.sensorType,
								sensorTypeId: installation.sensorTypeId,
								serialNumber: installation.serialNumber,
								installationDate: null,
								rssi: installation.rssi,
								tags: installation.tags,
								isOnline: false,
								location: {
									floorName: installation.sensorLocationFloor,
									roomName: installation.sensorLocationRoom,
									roomId: room.id,
									floorId: '',
								},
								status: 'PLANNED',
							};
						});
				})
				.filter(installation => installation.length)
				.flat();

			if (installationItems.length) {
				building.sensors = building.sensors.concat(installationItems);
			}
		});

		return buildingsDeepCopy;
	}

	static isObject(item): boolean {
		if (!item) {
			return false;
		}
		return typeof item === 'object' && !Array.isArray(item);
	}

	static normalizeString(item: string): string {
		if (!item) {
			return '';
		}
		return item.toString().trim().toLocaleLowerCase();
	}

	static onlyUnique(value, index, self) {
		return self.indexOf(value) === index;
	}

	static capitalizeText(text: string): string {
		return text.charAt(0).toUpperCase() + text.slice(1);
	}

	static round(value: number, decimals: number = 0): number {
		// @ts-ignore
		return Number(`${Math.round(`${value}e${decimals}`)}e-${decimals}`);
	}

	static isAutilityAuth(): boolean {
		return environment.authenticationBy === AuthenticationBy.autility;
	}

	static isKeycloakAuth(): boolean {
		return environment.authenticationBy === AuthenticationBy.keycloak;
	}

	static isNotEqual(actual, previous): boolean {
		return JSON.stringify(actual) !== JSON.stringify(previous);
	}

	static isEqual(actual, previous): boolean {
		return JSON.stringify(actual) === JSON.stringify(previous);
	}

	static objectEmpty(obj: { [key: string]: any }): boolean {
		return Object.keys(obj).length === 0;
	}

	static objectNotEmpty(obj: { [key: string]: any }): boolean {
		return Object.keys(obj).length !== 0;
	}

	static insertAtIndex(arr: any[], index: number, ...items: any): void {
		arr.splice(index, 0, ...items);
	}

	static getColorBasedByUuidColor(uuid: string): string {
		/** We want to have a colored avatars based on some never changing marker.
		 *  For example uuid */
		if (!uuid?.length) {
			return AuPlaceholdersColorsList[0];
		}
		/** Here we take UTF-16 code from every symbol */
		const a = [];
		for (let i = 0; i < uuid.length; i++) {
			a.push(uuid.charCodeAt(i));
		}
		/** Sum it, take last digit and use it as an index for color from color array */
		const index = a.reduce((partialSum, a) => partialSum + a, 0) % 10;
		return AuPlaceholdersColorsList[index];
	}

	static excludeObjectKeys(obj: any, keysToExclude: string[]): { [key: string]: any } {
		return Object.keys(obj).reduce((result, key) => {
			if (!keysToExclude.includes(key)) {
				result[key] = obj[key];
			}
			return result;
		}, {});
	}

	static filterObjUndefinedValues(obj: { [key: string]: any }): {
		[key: string]: any;
	} {
		if (!obj) {
			return {};
		}
		const filteredEntries = Object.entries(obj).filter(
			([, value]) => value !== undefined
		);
		return Object.fromEntries(filteredEntries);
	}

	static isDateBeforeToday(date: Date): boolean {
		const inputDate = new Date(date);
		const today = new Date();

		// Set the time of both dates to 0 to ignore it
		inputDate.setHours(0, 0, 0, 0);
		today.setHours(0, 0, 0, 0);

		return inputDate < today;
	}

	static extractNodes(edges: any[]): any[] {
		return edges?.map(edge => edge.node);
	}

	static naturalSort(a: string, b: string) {
		const nameA = a.trim();
		const nameB = b.trim();

		return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
	}

	static diff<T>(a: T, b: T): Partial<T> {
		const result: Partial<T> = {};

		if (AuUtilsFunctions.isObject(a) && AuUtilsFunctions.isObject(b)) {
			for (const key in a) {
				const leftValue = a[key];
				const rightValue = b[key];
				let same = leftValue === rightValue;
				if (a instanceof Object && b instanceof Object) {
					same = AuUtilsFunctions.isEqual(leftValue, rightValue);
				}
				if (!same) {
					result[key] = b[key];
				}
			}
		}

		return result;
	}

	// This will check if any new significant values have been added to the object, ignoring empty strings, nulls and empty arrays
	static hasBeenFilled(initialValue: any, currentValue: any): boolean {
		const diff = AuUtilsFunctions.diff(initialValue, currentValue);
		const numOfFilledValues = Object.values(diff)
			.map(value => {
				if (value === null || value === '' || (Array.isArray(value) && !value?.length)) {
					return 0;
				}
				return 1;
			})
			.reduce((a, b) => a + b, 0);
		return !!numOfFilledValues;
	}
}
